import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, XCircle } from 'lucide-react'
import { Turnstile } from '@/components/ui/Turnstile'

const API_URL = import.meta.env.VITE_API_BASE_URL || '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

interface StatusMessage {
  type: 'success' | 'error';
  text: string;
}

const phrases = [
  {
    quote: 'Validea te ayuda a descubrir si tu idea tiene tracción real antes de invertir tiempo y dinero.',
    author: 'Sofía Méndez',
    role: 'Fundadora de StartUpX',
  },
  {
    quote: 'Prioriza las funcionalidades que tus usuarios realmente quieren con datos, no suposiciones.',
    author: 'Pablo Torres',
    role: 'Product Manager',
  },
  {
    quote: 'Valida tu MVP en días, no meses, y toma decisiones basadas en evidencia.',
    author: 'Laura Ruiz',
    role: 'Emprendedora Serial',
  },
]

export default function ForgotPassword() {
  const [current, setCurrent] = useState(0)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null)
  const [turnstileToken, setTurnstileToken] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!turnstileToken) {
      setStatusMessage({
        type: 'error',
        text: 'Por favor, completa el captcha.'
      });
      return;
    }

    setIsSubmitting(true)
    setStatusMessage(null)

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          turnstileToken 
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setStatusMessage({
          type: 'success',
          text: data.message
        });
        setEmail('');
      } else {
        setStatusMessage({
          type: 'error',
          text: data.message
        });
      }
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: 'Error al procesar la solicitud. Por favor, intenta de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Columna izquierda */}
      <div className="hidden md:flex flex-col justify-center items-start flex-1 bg-gradient-to-br from-[#6366F1] to-[#4338CA] px-16">
        <h1 className="text-4xl font-extrabold text-white mb-8">Bienvenido a Validea</h1>
        <blockquote className="text-white text-xl font-light mb-6 max-w-lg">“{phrases[current].quote}”</blockquote>
        <div className="mb-2">
          <span className="text-white font-semibold">{phrases[current].author}</span><br />
          <span className="text-white text-sm">{phrases[current].role}</span>
        </div>
        {/* Slider dots */}
        <div className="flex gap-2 mt-6">
          {phrases.map((_, idx) => (
            <button
              key={idx}
              className={`h-3 w-3 rounded-full border-2 ${current === idx ? 'bg-white border-white' : 'bg-transparent border-white opacity-60'} transition-all`}
              onClick={() => setCurrent(idx)}
              aria-label={`Frase ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      {/* Columna derecha */}
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-[#6366F1] text-center mb-2">Validea</h2>
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-1">Recuperar contraseña</h3>
          <p className="text-center text-gray-500 text-sm mb-6">
            Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.<br />
            <Link to="/login" className="text-[#6366F1] hover:underline">Volver a iniciar sesión</Link>
          </p>

          {statusMessage && (
            <div 
              className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                statusMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
              role="alert"
            >
              {statusMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="flex-1">{statusMessage.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Correo electrónico
              </label>
              <input 
                type="email" 
                id="email" 
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" 
                required 
                placeholder="tu@email.com"
                disabled={isSubmitting}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#6366F1] focus:ring-[#6366F1] disabled:opacity-50 disabled:cursor-not-allowed" 
              />
            </div>

            {/* Turnstile captcha */}
            <div className="mt-4">
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={setTurnstileToken}
                theme="light"
                className="flex justify-center"
              />
            </div>

            <button 
              type="submit"
              disabled={isSubmitting || !turnstileToken} 
              className="w-full bg-[#6366F1] hover:bg-[#4338CA] text-white font-semibold py-2 rounded-md mt-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar instrucciones'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
} 